import CustomButton from "@components/buttons/CustomButton";
import { CurrencyLogoDropdown } from "@components/dropdowns/CurrencyLogoDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { transactionApiServices } from "@services/apis/Transactions";
import { Network } from "@services/models/Transactions/GetNetworkList";
import { ValidateTransactionDetialsRequest } from "@services/models/Transactions/ValidateTransactionDetails";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

interface MissingTransactionProps {
  isOpen: boolean;
  onClose: () => void;
}

type ValidationState = "none" | "success" | "error";

const MissingTransactionSlider = ({
  isOpen,
  onClose,
}: MissingTransactionProps) => {
  const [transactionHash, setTransactionHash] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [transactionValidated, setTransactionValidated] =
    useState<ValidationState>("none");
  const [message, setMessage] = useState<string>("");

  const [orderId, setOrderId] = useState<string>("");

  const [selectedNetworkId, setSelectedNetworkId] = useState<
    number | undefined
  >(undefined);
  const [selectedNetwork, setSelectedNetwork] = useState<Network | undefined>(
    undefined
  );
  const [networkLoading, setNetworkLoading] = useState<boolean>(true);
  const [networks, setNetworks] = useState<Network[]>([]);

  const fetchNetworkList = async () => {
    try {
      setNetworkLoading(true);
      const response = await transactionApiServices.getNetworksList();
      setNetworks(response.data);
      setSelectedNetworkId(response.data[0]?.networkId);
      setSelectedNetwork(response.data[0]);
      setNetworkLoading(false);
    } catch (error) {
      setNetworkLoading(false);
    }
  };

  const handleNetworkSelection = async (value: string | number) => {
    const selectedNetworkId = Number(value);
    setSelectedNetworkId(selectedNetworkId);
    const selectedNetwork = networks.find(
      (network) => network.networkId === selectedNetworkId
    );
    setSelectedNetwork(selectedNetwork);
  };

  const handleOnClick = async () => {
    try {
      setLoading(true);
      const requestData: ValidateTransactionDetialsRequest = {
        transactionHash: transactionHash,
        network: selectedNetwork?.networkSymbol || "",
        txOID: orderId,
      };
      const validateTransactionDetail =
        await transactionApiServices.validateTransactionDetails(requestData);
      if (validateTransactionDetail.status.toLowerCase() === "success") {
        setTransactionValidated("success");
      } else {
        setTransactionValidated("error");
      }
      setMessage(validateTransactionDetail.msg);
    } catch (error: any) {
      setTransactionValidated("error");
      setMessage(error?.response?.data?.msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNetworkList();
  }, []);

  return (
    <SlidingPanel
      showConfirm={false}
      isOpen={isOpen}
      isSlider={false}
      isMobile={false}
      heading={"Find missing transactions"}
      onClose={() => {
        onClose();
        setTransactionValidated("none");
        setTransactionHash("");
      }}
    >
      {transactionValidated !== "none" ? (
        <div className="flex flex-col gap-[16px] p-[16px]">
          <div
            className={`${
              transactionValidated === "success"
                ? "bg-bg-status-green"
                : "bg-bg-status-red"
            } rounded-lg flex flex-col items-center p-[16px]`}
          >
            <span className="text-[14px] text-text-base font-medium">
              {transactionValidated === "success"
                ? "Transaction found"
                : "Transaction not found"}
            </span>
            <div className="bg-[#15182C] rounded-full w-[40px] h-[40px] mt-[20px] flex items-center justify-center ">
              {transactionValidated === "success" ? (
                <span className="material-symbols-outlined text-bg-page-body">
                  check
                </span>
              ) : (
                <span className="material-symbols-outlined text-bg-page-body">
                  close
                </span>
              )}
            </div>
            <span className="text-[12px] text-text-base font-normal text-center mt-[20px]">
              {message ||
                (transactionValidated === "success"
                  ? "Based on the details submitted, the following transaction has been found and added to your wallet."
                  : "Based on the details submitted, no transaction has been found. Please recheck the details and submit again.")}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col p-[16px] gap-[16px] overflow-y-auto">
          <span className="text-[12px] font-medium text-text-base">
            Submit the details below to locate your missing transactions.
          </span>
          {!networkLoading ? (
            <CurrencyLogoDropdown
              listOfValues={networks.map((network) => ({
                id: network.networkId,
                currency: network.networkSymbol,
                currencyLogo: network.networkImg,
              }))}
              value={selectedNetworkId ?? undefined}
              onChange={handleNetworkSelection}
              label="Select network"
              color="bg-main-container-header"
              baseColor="bg-bg-page-body"
              borderColor="border-subtle"
              expandedBorderColor="border-button-primary"
            />
          ) : (
            <Skeleton height={44} className="mb-1" />
          )}
          <GlobalTextInputField
            type="text"
            title="Transaction hash"
            placeholder="Enter transaction hash here"
            value={transactionHash}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTransactionHash(e.target.value)
            }
          />
          <GlobalTextInputField
            type="text"
            title="Transaction Order ID"
            placeholder="Enter transaction order ID here"
            value={orderId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOrderId(e.target.value)
            }
          />
          <CustomButton
            isActive={true}
            isButtonLoading={loading}
            onClick={() => handleOnClick()}
            text="FIND TRANSACTION"
          />
        </div>
      )}
    </SlidingPanel>
  );
};

export default MissingTransactionSlider;
